import { useCallback, useEffect, useState } from 'react'
import { getFeatures, getFloors, getStyle } from '@/lib/api'
import type { ProximiioFeatureCollection, ProximiioFloor, ProximiioStyleResponse } from '@/types/proximi'

interface MapData {
  style: ProximiioStyleResponse | null
  features: ProximiioFeatureCollection | null
  floors: ProximiioFloor[]
  loading: boolean
  error: string | null
  reload: () => void
}

const EMPTY: ProximiioFeatureCollection = { type: 'FeatureCollection', features: [] }

/**
 * Loads the three things the map needs before it can draw: the style document,
 * the GeoJSON features that fill its `main` source, and the floor list.
 *
 * Floors are best-effort — a style renders fine without them, and the level
 * switcher falls back to the levels actually present in the features.
 */
export function useMapData(): MapData {
  const [style, setStyle] = useState<ProximiioStyleResponse | null>(null)
  const [features, setFeatures] = useState<ProximiioFeatureCollection | null>(null)
  const [floors, setFloors] = useState<ProximiioFloor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([getStyle(), getFeatures(), getFloors().catch(() => [] as ProximiioFloor[])])
      .then(([styleResponse, featureCollection, floorList]) => {
        if (cancelled) return
        setStyle(styleResponse)
        setFeatures(featureCollection ?? EMPTY)
        setFloors([...floorList].sort((a, b) => a.level - b.level))
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [nonce])

  return { style, features, floors, loading, error, reload }
}
